import {
  Autocomplete,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setManualEditReceiptsPayload,
  setEditReceipts,
  setLinkedFmes,
  setEditedReceiptsInitialized,
} from "../../../../../features/financeReceiptSlice";
import dayjs from "dayjs";

const status = [{ label: "Final" }, { label: "Not Final" }];
const ReceiptForm = ({
  receipt,
  handleFieldChange,
  companyOptions,
  currencyOptions,
  handleCompanyChange,
}: any) => {
  const companySearchValue = String(
    receipt.companySearch ?? receipt.company?.label ?? "",
  );
  const selectedCompanyLabel = receipt.company?.label ?? "";
  const companyFilterValue =
    companySearchValue === selectedCompanyLabel ? "" : companySearchValue;

  const filteredCompanyOptions = companyFilterValue
    ? (companyOptions || []).filter((option: any) =>
        option.label.toLowerCase().includes(companyFilterValue.toLowerCase()),
      )
    : companyOptions || [];

  return (
    <Grid
      container
      spacing={2}
      mb={2}
      pb={3}
      sx={{ borderBottom: "1px solid #ccc" }}
    >
      <Grid size={12}>
        <Typography
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Receipt Details</span>
        </Typography>
      </Grid>

      <Grid size={4}>
        <TextField
          label="Receipt Number"
          value={receipt.receiptNumber}
          color="secondary"
          onChange={(e) =>
            handleFieldChange(receipt.id, "receiptNumber", e.target.value)
          }
          placeholder="The transaction serial number "
          fullWidth
          sx={{ mb: 2 }}
          size="small"
        />
        <TextField
          label="Receipt Amount"
          value={receipt.receiptAmount}
          color="secondary"
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d*$/.test(value)) {
              handleFieldChange(receipt.id, "receiptAmount", value);
            }
          }}
          placeholder="The sum listed on the receipt"
          fullWidth
          size="small"
          inputProps={{
            inputMode: "decimal",
            pattern: "[0-9.]*",
          }}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          sx={{ width: "100%", mb: 2 }}
          options={filteredCompanyOptions}
          value={receipt.company}
          inputValue={companySearchValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input" || reason === "clear") {
              handleFieldChange(receipt.id, "companySearch", newInputValue);
            }
          }}
          filterOptions={(options) => options}
          isOptionEqualToValue={(option, value) =>
            option?.shared_uid === value?.shared_uid
          }
          onChange={(_, newValue) => handleCompanyChange(receipt.id, newValue)}
          getOptionLabel={(option) => option?.label ?? ""}
          renderOption={(props, option) => {
            const { key: _key, ...optionProps } = props;
            void _key;
            return (
              <li
                key={`${option.shared_uid}-${option.finance_company_id}`}
                {...optionProps}
              >
                {option.label}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Finance Company Name"
              placeholder="Who issued this receipt"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ marginLeft: "12px" }} />
                  </InputAdornment>
                ),
              }}
              color="secondary"
            />
          )}
          size="small"
        />

        <Autocomplete
          size="small"
          options={currencyOptions || []}
          value={receipt.currency}
          isOptionEqualToValue={
            (option, value) => option?.shared_uid === value?.shared_uid // Add this
          }
          onChange={(_, newValue) =>
            handleFieldChange(receipt.id, "currency", newValue)
          }
          getOptionLabel={(option) => `${option.symbol} ${option.code}`}
          renderOption={(props, option) => (
            <li {...props}>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 500,
                  marginRight: "8px",
                }}
              >
                {option.symbol}
              </span>
              {option.code}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Currency"
              color="secondary"
              placeholder="Choose from the list"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          sx={{ width: "100%", mb: 1 }}
          options={status}
          value={receipt.status}
          onChange={(_, newValue) =>
            handleFieldChange(receipt.id, "status", newValue)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Status"
              placeholder="State of collection"
              value={""}
              // sx={{ width: "300px" }}
              InputProps={{
                ...params.InputProps,
              }}
              color="secondary"
            />
          )}
          size="small"
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <DatePicker
              format="DD/MM/YYYY"
              value={receipt.date}
              onChange={(newValue) =>
                handleFieldChange(receipt.id, "date", newValue)
              }
              slotProps={{
                textField: {
                  size: "small",
                  color: "secondary",
                  // required: true,
                },
              }}
              sx={{ width: "100%" }}
              label="Date"
            />
          </DemoContainer>
        </LocalizationProvider>
      </Grid>
    </Grid>
  );
};

const EditReceiptsDetails = () => {
  const dispatch = useDispatch();

  const {
    allCurrencies,
    allCompanies,
    editReceipts,
    selectedReceipt,
    editReceiptsInitialized,
  } = useSelector((state: any) => state.receipts);
  const companyOptions = useMemo(
    () =>
      allCompanies
        ?.filter(
          (company: any) =>
            company?.finance_company_name?.trim() &&
            company?.finance_company_id != null,
        )
        .map((company: any) => ({
          label: company.finance_company_name.trim(),
          shared_uid: company.shared_uid,
          finance_company_id: company.finance_company_id,
        })) || [],
    [allCompanies],
  );
  const currencyOptions = useMemo(
    () =>
      allCurrencies?.map((currency: any) => ({
        code: currency.currency_code,
        symbol: currency.currency_symbol,
        shared_uid: currency.shared_uid,
      })),
    [allCurrencies],
  );

  const handleFieldChange = (id: any, field: any, value: any) => {
    dispatch(
      setEditReceipts(
        editReceipts.map((receipt: any) =>
          receipt.id === id ? { ...receipt, [field]: value } : receipt,
        ),
      ),
    );
  };
  const handleCompanyChange = (id: any, company: any) => {
    dispatch(
      setEditReceipts(
        editReceipts.map((receipt: any) =>
          receipt.id === id
            ? {
                ...receipt,
                company,
                companySearch: company?.label ?? "",
              }
            : receipt,
        ),
      ),
    );
  };
  const buildPayload = () => {
    return editReceipts.map((receipt: any) => ({
      receipt_id: receipt.receiptNumber,
      receipt_date: receipt.date ? receipt.date.format("YYYY-MM-DD") : null,
      receipt_amount: receipt.receiptAmount
        ? Number(receipt.receiptAmount)
        : null,
      receipt_currency_shared_uid: receipt.currency?.shared_uid ?? null,
      company_shared_uid: receipt.company?.shared_uid ?? null,
      receipt_status: receipt.status?.label ?? null,
    }));
  };

  useEffect(() => {
    const payload = buildPayload();
    dispatch(setManualEditReceiptsPayload(payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editReceipts, dispatch]);

  useEffect(() => {
    if (!selectedReceipt) return;
    const matchedCompany =
      companyOptions?.find(
        (c: any) => c.shared_uid === selectedReceipt.company_shared_uid,
      ) ??
      (selectedReceipt.company_name
        ? {
            label: selectedReceipt.finance_company_name,
            shared_uid: selectedReceipt.company_shared_uid,
          }
        : null);

    const matchedCurrency =
      currencyOptions?.find(
        (c: any) =>
          c.shared_uid === selectedReceipt.receipt_currency_shared_uid,
      ) ??
      (selectedReceipt.receipt_currency_code ||
      selectedReceipt.receipt_currency_symbol
        ? {
            code: selectedReceipt.receipt_currency_code,
            symbol: selectedReceipt.receipt_currency_symbol,
            shared_uid: selectedReceipt.receipt_currency_shared_uid,
          }
        : null);

    const mapped = [
      {
        id: 1,
        receiptNumber: selectedReceipt.receipt_id ?? "",
        receiptAmount:
          selectedReceipt.receipt_amount !== undefined &&
          selectedReceipt.receipt_amount !== null
            ? String(selectedReceipt.receipt_amount)
            : "",
        company: matchedCompany,
        currency: matchedCurrency,
        status: { label: selectedReceipt.receipt_status ?? "Not Final" },
        date: selectedReceipt.receipt_date
          ? dayjs(selectedReceipt.receipt_date)
          : null,
        companySearch: matchedCompany?.label ?? "",
      },
    ];
    if (!editReceiptsInitialized && selectedReceipt?.receipt_shared_uid) {
      dispatch(setEditReceipts(mapped));
      dispatch(setEditedReceiptsInitialized(true));
    }

    if (selectedReceipt?.fmes?.length) {
      dispatch(setLinkedFmes(selectedReceipt.fmes));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {editReceipts.map((receipt: any) => (
        <ReceiptForm
          key={receipt.id}
          receipt={receipt}
          handleFieldChange={handleFieldChange}
          handleCompanyChange={handleCompanyChange}
          companyOptions={companyOptions}
          currencyOptions={currencyOptions}
        />
      ))}
    </>
  );
};

export default EditReceiptsDetails;
